/**
 * WorkflowStepCompliance (Step 5)
 *
 * Compliance preflight check with categorized issues.
 * Substeps: documentChecks, fieldChecks, jurisdictionChecks, licenseChecks, tradeChecks
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { orderBy } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  ShieldCheck,
  FileText,
  AlertTriangle,
  XCircle,
  CheckCircle,
  RefreshCw,
  ArrowRight,
  Loader2,
  MapPin,
  CreditCard,
  Wrench,
  Upload,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRunComplianceCheck } from '@/hooks/useCompliance';
import { mapComplianceFindingsToIssues } from '@/types/workflow';
import { dualReadJobDocuments } from '@/lib/firestore-helpers';
import type { UseJobWorkflowReturn } from '@/hooks/useJobWorkflow';
import type { JobData } from '../WorkflowContainer';
import type { WorkflowIssue, IssueCategory } from '@/types/workflow';

// Document from Firestore
interface JobDocument {
  id: string;
  category: string | null;
  status: string | null;
}

// Get required documents based on trade/permit type (same as WorkflowStepDocuments)
function getRequiredDocumentsForTrade(trade: string | undefined): Array<{ category: string; name: string; required: boolean }> {
  const baseRequirements = [
    { category: 'application_form', name: 'Permit Application Form', required: true },
    { category: 'contractor_license', name: 'Contractor License', required: true },
    { category: 'insurance_coi', name: 'Certificate of Insurance', required: true },
    { category: 'owner_auth', name: 'Owner Authorization', required: false },
  ];

  switch (trade) {
    case 'electrical':
      return [
        ...baseRequirements,
        { category: 'load_calculation', name: 'Load Calculation', required: true },
        { category: 'single_line', name: 'Single Line Diagram', required: true },
        { category: 'riser_diagram', name: 'Riser Diagram', required: false },
      ];
    case 'mechanical':
      return [
        ...baseRequirements,
        { category: 'load_calculation', name: 'Manual J/D/S Calculation', required: true },
        { category: 'equipment_specs', name: 'Equipment Specifications', required: true },
        { category: 'energy_calc', name: 'Energy Calculation', required: false },
      ];
    case 'plumbing':
      return [
        ...baseRequirements,
        { category: 'riser_diagram', name: 'Plumbing Riser', required: true },
        { category: 'site_plan', name: 'Site Plan', required: false },
      ];
    case 'roofing':
      return [
        ...baseRequirements,
        { category: 'product_approval', name: 'Product Approval (NOA)', required: true },
        { category: 'sealed_drawings', name: 'Sealed Drawings', required: false },
      ];
    case 'building':
    default:
      return [
        ...baseRequirements,
        { category: 'building_plans', name: 'Building Plans', required: true },
        { category: 'site_plan', name: 'Site Plan', required: false },
        { category: 'survey', name: 'Property Survey', required: false },
      ];
  }
}

interface WorkflowStepComplianceProps {
  jobId: string;
  contractorId: string;
  jobData: JobData;
  workflow: UseJobWorkflowReturn;
  onRerunCheck?: () => void;
}

// Category configuration
const categoryConfig: Record<IssueCategory, { label: string; icon: React.ReactNode }> = {
  document: { label: 'Documents', icon: <FileText className="h-4 w-4" /> },
  field: { label: 'Required Fields', icon: <FileText className="h-4 w-4" /> },
  jurisdiction: { label: 'Jurisdiction', icon: <MapPin className="h-4 w-4" /> },
  license: { label: 'License', icon: <CreditCard className="h-4 w-4" /> },
  trade: { label: 'Trade Requirements', icon: <Wrench className="h-4 w-4" /> },
};

export function WorkflowStepCompliance({
  jobId,
  contractorId,
  jobData,
  workflow,
  onRerunCheck,
}: WorkflowStepComplianceProps) {
  const [lastCheckTime, setLastCheckTime] = useState<string | null>(null);
  const [hasRunCheck, setHasRunCheck] = useState(false);
  const [hasAcknowledgedMissingDocs, setHasAcknowledgedMissingDocs] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<JobDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Use the real compliance check hook
  const { mutate: runComplianceCheck, isPending: isChecking } = useRunComplianceCheck();

  const issues = workflow.getIssuesForCurrentStep();
  const allIssues = [...issues.blockers, ...issues.warnings, ...issues.info];

  // Get required documents based on trade
  const requiredDocuments = useMemo(
    () => getRequiredDocumentsForTrade(jobData.trade || jobData.permitType || undefined),
    [jobData.trade, jobData.permitType]
  );

  // Fetch uploaded documents from Firestore
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!contractorId || !jobId) {
        setDocumentsLoading(false);
        return;
      }

      try {
        setDocumentsLoading(true);
        const snapshot = await dualReadJobDocuments(contractorId, jobId, [
          orderBy('createdAt', 'desc')
        ]);

        const docs = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            category: data.category || data.complianceCategory || null,
            status: data.status || null,
          } as JobDocument;
        });

        setUploadedDocuments(docs);
      } catch (error) {
        console.error('Error fetching documents for compliance:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocuments();
  }, [contractorId, jobId]);

  // Calculate missing required documents
  const missingDocuments = useMemo(() => {
    return requiredDocuments
      .filter(req => req.required)
      .filter(req => !uploadedDocuments.some(doc => doc.category === req.category))
      .map(req => req.name);
  }, [requiredDocuments, uploadedDocuments]);

  const allRequiredDocsUploaded = missingDocuments.length === 0;

  // Check if basic prerequisites are met (required data from previous steps)
  const basicPrerequisites = checkBasicPrerequisites(jobData);

  // Full prerequisites: basic data + all required documents
  const canRunCheck = basicPrerequisites.met && (allRequiredDocsUploaded || hasAcknowledgedMissingDocs);

  // Group issues by category
  const issuesByCategory = allIssues.reduce((acc, issue) => {
    if (!acc[issue.category]) {
      acc[issue.category] = [];
    }
    acc[issue.category].push(issue);
    return acc;
  }, {} as Record<IssueCategory, WorkflowIssue[]>);

  // Calculate compliance readiness (0% until check has run, or show prerequisite status)
  const totalChecks = 5; // Number of check categories
  const passingChecks = hasRunCheck
    ? Object.keys(categoryConfig).filter(
        (cat) => !issuesByCategory[cat as IssueCategory]?.some((i) => i.severity === 'blocker')
      ).length
    : 0;
  const compliancePercent = hasRunCheck
    ? Math.round((passingChecks / totalChecks) * 100)
    : 0;

  // Update progress
  useEffect(() => {
    workflow.updateProgress({ complianceReadinessPct: compliancePercent });

    if (hasRunCheck && issues.blockers.length === 0) {
      workflow.markSubstepComplete('compliance', 'documentChecks');
      workflow.markSubstepComplete('compliance', 'fieldChecks');
      workflow.markSubstepComplete('compliance', 'jurisdictionChecks');
      workflow.markSubstepComplete('compliance', 'licenseChecks');
      workflow.markSubstepComplete('compliance', 'tradeChecks');
    }
  }, [compliancePercent, issues.blockers.length, workflow, hasRunCheck]);

  // Build missing document issues
  const getMissingDocumentIssues = useCallback((): WorkflowIssue[] => {
    if (allRequiredDocsUploaded) return [];

    return requiredDocuments
      .filter(req => req.required)
      .filter(req => !uploadedDocuments.some(doc => doc.category === req.category))
      .map((req) => ({
        id: `missing-doc-${req.category}`,
        severity: 'blocker' as const,
        category: 'document' as IssueCategory,
        message: `Missing required document: ${req.name}`,
        details: 'This document must be uploaded before permit submission.',
        fixStepId: 'documents' as const,
        fixAction: 'Upload',
        source: 'document_check',
      }));
  }, [requiredDocuments, uploadedDocuments, allRequiredDocsUploaded]);

  // Handle running the compliance check
  const handleRunCheck = useCallback(() => {
    if (!canRunCheck) return;

    runComplianceCheck(
      {
        jobId,
        contractorId,
        permitType: jobData.permitType || undefined,
        jobScope: jobData.jobScope || undefined,
      },
      {
        onSuccess: (result) => {
          setLastCheckTime(new Date().toISOString());
          setHasRunCheck(true);

          // Start with any missing document issues (if user proceeded anyway)
          const missingDocIssues = getMissingDocumentIssues();

          // Map compliance findings to workflow issues
          if (result.findings && result.findings.length > 0) {
            const mappedIssues = mapComplianceFindingsToIssues(result.findings);
            workflow.setIssues(
              [...missingDocIssues, ...mappedIssues.blockers],
              mappedIssues.warnings,
              mappedIssues.info
            );
          } else {
            // No findings from API - only show missing document issues
            workflow.setIssues(missingDocIssues, [], []);
          }

          // Callback to parent if provided
          if (onRerunCheck) {
            onRerunCheck();
          }
        },
        onError: (error) => {
          console.error('Compliance check failed:', error);
          setHasRunCheck(true);

          // Include missing document issues even on error
          const missingDocIssues = getMissingDocumentIssues();

          // Add error as a blocker issue
          workflow.setIssues(
            [
              ...missingDocIssues,
              {
                id: 'compliance-error',
                severity: 'blocker',
                category: 'field',
                message: 'Compliance check failed',
                details: error instanceof Error ? error.message : 'Unable to run compliance check',
                fixStepId: 'compliance',
                fixAction: 'Retry Check',
                source: 'compliance_check',
              },
            ],
            [],
            []
          );
        },
      }
    );
  }, [jobId, contractorId, jobData.permitType, jobData.jobScope, runComplianceCheck, workflow, onRerunCheck, canRunCheck, getMissingDocumentIssues]);

  // Check basic prerequisites from previous steps (not including document completeness)
  function checkBasicPrerequisites(data: JobData): { met: boolean; missing: { item: string; step: string }[] } {
    const missing: { item: string; step: string }[] = [];

    // Required from Basics step
    if (!data.location || data.location.trim() === '') {
      missing.push({ item: 'Project address', step: 'Basics' });
    }
    if (!data.jurisdictionId) {
      missing.push({ item: 'Jurisdiction', step: 'Basics' });
    }

    // Required from Scope step
    if (!data.permitType) {
      missing.push({ item: 'Permit type', step: 'Scope' });
    }

    return {
      met: missing.length === 0,
      missing,
    };
  }

  return (
    <div className="space-y-6">
      {/* Compliance Score Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Compliance Preflight
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunCheck}
              disabled={isChecking || !canRunCheck}
            >
              {isChecking ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              {hasRunCheck ? 'Re-run Check' : 'Run Check'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  'w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold',
                  !hasRunCheck
                    ? 'bg-muted text-muted-foreground'
                    : compliancePercent === 100
                    ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-200'
                    : compliancePercent >= 50
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200'
                    : 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-200'
                )}
              >
                {hasRunCheck ? `${compliancePercent}%` : '—'}
              </div>
              <div className="flex-1">
                <Progress
                  value={compliancePercent}
                  className={cn(
                    'h-3',
                    compliancePercent === 100 && '[&>div]:bg-green-500'
                  )}
                />
                <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                  <span>
                    {hasRunCheck ? issues.blockers.length : '—'} Blockers
                  </span>
                  <span>
                    {hasRunCheck ? issues.warnings.length : '—'} Warnings
                  </span>
                  <span>
                    {hasRunCheck ? issues.info.length : '—'} Info
                  </span>
                </div>
              </div>
            </div>

            {lastCheckTime && (
              <p className="text-xs text-muted-foreground">
                Last checked: {new Date(lastCheckTime).toLocaleString()}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading Documents State */}
      {documentsLoading && (
        <Card className="border-border bg-muted/30">
          <CardContent className="py-8">
            <div className="text-center">
              <Loader2 className="h-12 w-12 mx-auto text-muted-foreground mb-4 animate-spin" />
              <h3 className="text-lg font-semibold text-foreground">Checking Document Status...</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Verifying uploaded documents against requirements.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Basic Prerequisites Not Met State */}
      {!documentsLoading && !basicPrerequisites.met && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardContent className="py-8">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
              <h3 className="text-lg font-semibold text-amber-700 dark:text-amber-200">
                Complete Previous Steps First
              </h3>
              <p className="text-sm text-amber-600 dark:text-amber-300 mt-1 mb-4">
                The following is required before running compliance checks:
              </p>
              <ul className="text-sm text-amber-600 dark:text-amber-300 space-y-2">
                {basicPrerequisites.missing.map((missing) => (
                  <li key={missing.item} className="flex items-center justify-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                    <span>{missing.item}</span>
                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 dark:border-amber-600 dark:text-amber-300">
                      {missing.step}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Missing Required Documents State */}
      {!documentsLoading && basicPrerequisites.met && !allRequiredDocsUploaded && !hasAcknowledgedMissingDocs && (
        <Card className="border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/30">
          <CardContent className="py-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-red-700 dark:text-red-200">
                Required Documents Missing
              </h3>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1 mb-4">
                The following required documents have not been uploaded:
              </p>
              <ul className="text-sm text-red-600 dark:text-red-300 space-y-2 mb-6">
                {missingDocuments.map((docName) => (
                  <li key={docName} className="flex items-center justify-center gap-2">
                    <Upload className="h-4 w-4" />
                    <span>{docName}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  variant="default"
                  onClick={() => workflow.goToStep('documents')}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Go to Documents
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setHasAcknowledgedMissingDocs(true)}
                  className="text-red-600 border-red-300 hover:bg-red-100 dark:text-red-300 dark:border-red-700 dark:hover:bg-red-950/50"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Proceed Anyway
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Proceeding without all documents will result in compliance issues that must be resolved before submission.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ready to Run Check State */}
      {!documentsLoading && canRunCheck && !hasRunCheck && !isChecking && (
        <Card className={cn(
          "border-border bg-muted/30",
          hasAcknowledgedMissingDocs && "border-amber-200 bg-amber-50/30 dark:border-amber-800 dark:bg-amber-950/20"
        )}>
          <CardContent className="py-8">
            <div className="text-center">
              <ShieldCheck className={cn(
                "h-12 w-12 mx-auto mb-4",
                hasAcknowledgedMissingDocs ? "text-amber-500" : "text-muted-foreground"
              )} />
              <h3 className="text-lg font-semibold text-foreground">Ready for Compliance Check</h3>
              {hasAcknowledgedMissingDocs ? (
                <p className="text-sm text-amber-600 dark:text-amber-300 mt-1 mb-4">
                  Proceeding with {missingDocuments.length} missing document{missingDocuments.length !== 1 ? 's' : ''}.
                  The compliance check will flag these as issues.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  All required documents are uploaded. Run the compliance check to validate your permit application.
                </p>
              )}
              <Button onClick={handleRunCheck} disabled={isChecking}>
                <ShieldCheck className="h-4 w-4 mr-2" />
                Run Compliance Check
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isChecking && (
        <Card className="border-border bg-muted/30">
          <CardContent className="py-8">
            <div className="text-center">
              <Loader2 className="h-12 w-12 mx-auto text-muted-foreground mb-4 animate-spin" />
              <h3 className="text-lg font-semibold text-foreground">Running Compliance Check...</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Validating your permit application against jurisdiction requirements.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Checks Passed State */}
      {hasRunCheck && !isChecking && allIssues.length === 0 && (
        <Card className="border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30">
          <CardContent className="py-8">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-semibold text-green-700 dark:text-green-200">All Checks Passed!</h3>
              <p className="text-sm text-green-600 dark:text-green-300 mt-1">
                Your permit application is ready for submission.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Issues by Category */}
      {hasRunCheck && !isChecking && allIssues.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Issues to Resolve
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" defaultValue={[]} className="w-full">
              {(Object.keys(categoryConfig) as IssueCategory[]).map((category) => {
                const categoryIssues = issuesByCategory[category] || [];
                const hasBlockers = categoryIssues.some((i) => i.severity === 'blocker');
                const hasWarnings = categoryIssues.some((i) => i.severity === 'warning');
                const config = categoryConfig[category];

                return (
                  <AccordionItem key={category} value={category}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 w-full">
                        <div
                          className={cn(
                            'p-2 rounded-lg',
                            hasBlockers
                              ? 'bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-200'
                              : hasWarnings
                              ? 'bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-200'
                              : 'bg-green-100 text-green-600 dark:bg-green-950/30 dark:text-green-200'
                          )}
                        >
                          {config.icon}
                        </div>
                        <span className="font-medium">{config.label}</span>
                        <div className="flex items-center gap-2 ml-auto mr-4">
                          {hasBlockers && (
                            <Badge variant="destructive" className="text-xs">
                              {categoryIssues.filter((i) => i.severity === 'blocker').length}{' '}
                              Blockers
                            </Badge>
                          )}
                          {hasWarnings && (
                            <Badge
                              variant="outline"
                              className="text-xs border-amber-500 text-amber-600 dark:border-amber-700 dark:text-amber-300"
                            >
                              {categoryIssues.filter((i) => i.severity === 'warning').length}{' '}
                              Warnings
                            </Badge>
                          )}
                          {!hasBlockers && !hasWarnings && (
                            <Badge
                              variant="outline"
                              className="text-xs border-green-500 text-green-600 dark:border-green-700 dark:text-green-300"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Passed
                            </Badge>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pl-11">
                        {categoryIssues.length === 0 ? (
                          <p className="text-sm text-green-600 dark:text-green-300 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            All {config.label.toLowerCase()} checks passed
                          </p>
                        ) : (
                          categoryIssues.map((issue) => (
                            <div
                              key={issue.id}
                              className={cn(
                                'p-3 rounded-lg border flex items-start justify-between gap-4',
                                issue.severity === 'blocker' &&
                                  'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/30',
                                issue.severity === 'warning' &&
                                  'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30',
                                issue.severity === 'info' &&
                                  'border-border bg-muted/40'
                              )}
                            >
                              <div className="flex items-start gap-3">
                                {issue.severity === 'blocker' && (
                                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                                )}
                                {issue.severity === 'warning' && (
                                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                )}
                                {issue.severity === 'info' && (
                                  <CheckCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                                )}
                                <div>
                                  <p
                                    className={cn(
                                      'font-medium',
                                      issue.severity === 'blocker' && 'text-red-700 dark:text-red-300',
                                      issue.severity === 'warning' && 'text-amber-700 dark:text-amber-300',
                                      issue.severity === 'info' && 'text-foreground'
                                    )}
                                  >
                                    {issue.message}
                                  </p>
                                  {issue.details && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {issue.details}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {issue.fixAction && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    workflow.goToStep(issue.fixStepId, issue.fixSubstepId)
                                  }
                                >
                                  {issue.fixAction}
                                  <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
